import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Paper,
  Tooltip,
  Checkbox,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import Loader from '../special/Loader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import MoneyLabel from '../label/MoneyLabel';
import { renderHours } from '../../scenes/time-tracking/worktime';
import PopUpTextEditor from '../popup/PopUpTextEditor';
import StyledTextContainer from '../preview/StyledTextContainer';

const TimeEntryReviewStep = ({
  selectedPresets,
  startDate,
  endDate,
  onBack,
  onConfirm,
  config,
  rounding,
  editMode = false,
  initialAdjustments = {},
  mode = 'chr', // 'chr' (calculative hourly rate) or 'positions' (work package based)
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const apiConfig = useConfig();

  const [timeTrackings, setTimeTrackings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adjustments, setAdjustments] = useState({});
  const [excludedFromReport, setExcludedFromReport] = useState({});
  const [lastInitialAdjustmentsKey, setLastInitialAdjustmentsKey] =
    useState('');
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState('');
  const [roundingAnchorEl, setRoundingAnchorEl] = useState(null);

  // Comment editor popup state
  const [commentEditorOpen, setCommentEditorOpen] = useState(false);
  const [editingCommentTT, setEditingCommentTT] = useState(null);

  // Collect all time tracking IDs from selected presets
  const allTimeTrackingIds = selectedPresets.reduce((acc, preset) => {
    return [...acc, ...(preset.timeTrackingIds || [])];
  }, []);

  // Fetch time tracking details
  useEffect(() => {
    if (allTimeTrackingIds.length === 0) {
      setIsLoading(false);
      return;
    }

    // Build query string with ids[]=X&ids[]=Y format
    const idsQuery = allTimeTrackingIds.map((id) => `ids[]=${id}`).join('&');

    DBRequest({
      config: apiConfig,
      path: `timetrackings?${idsQuery}`,
      method: API_GET,
      onResponse: (res) => {
        setTimeTrackings(res.data || []);
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  }, [apiConfig, allTimeTrackingIds.join(',')]);

  // Initialize adjustments and excludedFromReport from initialAdjustments (for edit mode)
  // FIX: Normalize times to ensure both start and end are in same format (without timezone)
  // This prevents timezone mismatch issues when only one time was adjusted
  useEffect(() => {
    if (!editMode) return;

    // Create a key to detect changes in initialAdjustments
    const currentKey = JSON.stringify(initialAdjustments);
    if (currentKey === lastInitialAdjustmentsKey) return;

    const newAdjustments = {};
    const newExcludedFromReport = {};

    Object.entries(initialAdjustments).forEach(([id, adj]) => {
      if (adj) {
        const timeStart = adj.time_start || adj.timeStart;
        const timeEnd = adj.time_end || adj.timeEnd;

        // FIX: If we have at least one time adjustment, normalize both to the same format
        // to prevent timezone mismatch issues
        if (timeStart || timeEnd) {
          // Find the corresponding time tracking to get original times if needed
          const tt = timeTrackings.find((t) => String(t.id) === String(id));
          const originalStart = tt?.timeStart || tt?.time_start;
          const originalEnd = tt?.timeEnd || tt?.time_end;

          // Format both times consistently (without timezone)
          const normalizedStart = timeStart
            ? moment(timeStart).format('YYYY-MM-DD HH:mm:ss')
            : originalStart
              ? moment(originalStart).format('YYYY-MM-DD HH:mm:ss')
              : null;
          const normalizedEnd = timeEnd
            ? moment(timeEnd).format('YYYY-MM-DD HH:mm:ss')
            : originalEnd
              ? moment(originalEnd).format('YYYY-MM-DD HH:mm:ss')
              : null;

          newAdjustments[id] = {
            comment: adj.comment,
            time_start: normalizedStart,
            time_end: normalizedEnd,
          };
        } else {
          newAdjustments[id] = {
            comment: adj.comment,
            time_start: null,
            time_end: null,
          };
        }

        if (adj.exclude_from_report || adj.excludeFromReport) {
          newExcludedFromReport[id] = true;
        }
      }
    });

    setAdjustments(newAdjustments);
    setExcludedFromReport(newExcludedFromReport);
    setLastInitialAdjustmentsKey(currentKey);
  }, [editMode, initialAdjustments, lastInitialAdjustmentsKey, timeTrackings]);

  // Build a map of preset by time tracking ID for wage info
  const presetByTimeTrackingId = {};
  selectedPresets.forEach((preset) => {
    (preset.timeTrackingIds || []).forEach((ttId) => {
      presetByTimeTrackingId[ttId] = preset;
    });
  });

  // Get the comment (adjusted or original)
  const getComment = (tt) => {
    return adjustments[tt.id]?.comment ?? tt.comment ?? '';
  };

  // Get time values (adjusted or original)
  const getTimeStart = (tt) => {
    return adjustments[tt.id]?.time_start ?? tt.timeStart;
  };

  const getTimeEnd = (tt) => {
    return adjustments[tt.id]?.time_end ?? tt.timeEnd;
  };

  // Check if entry is excluded from report
  const isExcludedFromReport = (tt) => {
    return excludedFromReport[tt.id] === true;
  };

  // Toggle exclude from report
  const toggleExcludeFromReport = (tt) => {
    setExcludedFromReport((prev) => ({
      ...prev,
      [tt.id]: !prev[tt.id],
    }));
  };

  // Calculate duration
  const getDuration = (tt) => {
    const start = moment(getTimeStart(tt));
    const end = moment(getTimeEnd(tt));
    return end.diff(start, 'hours', true);
  };

  // Check if a specific field has a real adjustment (different from original)
  const hasFieldAdjustment = (tt, field) => {
    const adj = adjustments[tt.id]?.[field];
    if (adj === undefined) return false;

    if (field === 'comment') {
      return adj !== (tt.comment ?? '');
    }
    if (field === 'time_start') {
      return adj !== tt.timeStart;
    }
    if (field === 'time_end') {
      return adj !== tt.timeEnd;
    }
    return false;
  };

  // Check if entry has any real adjustments (different from original values)
  const hasAnyAdjustment = (tt) => {
    return (
      hasFieldAdjustment(tt, 'comment') ||
      hasFieldAdjustment(tt, 'time_start') ||
      hasFieldAdjustment(tt, 'time_end')
    );
  };

  // Start editing a cell
  const handleStartEdit = (tt, field) => {
    setEditingCell({ id: tt.id, field });
    if (field === 'comment') {
      setEditValue(getComment(tt));
    } else if (field === 'time_start') {
      setEditValue(moment(getTimeStart(tt)).format('HH:mm'));
    } else if (field === 'time_end') {
      setEditValue(moment(getTimeEnd(tt)).format('HH:mm'));
    }
  };

  // Save edit
  const handleSaveEdit = (tt) => {
    if (!editingCell) return;

    const { field } = editingCell;
    let valueToSave = editValue;

    // For time fields, convert HH:mm to full datetime
    if (field === 'time_start' || field === 'time_end') {
      // FIX: Use current (adjusted or original) time, not raw original
      // This preserves the date from the adjustment when editing a draft
      const currentTime =
        field === 'time_start' ? getTimeStart(tt) : getTimeEnd(tt);
      const currentMoment = moment(currentTime);
      const [hours, minutes] = editValue.split(':').map(Number);

      if (!isNaN(hours) && !isNaN(minutes)) {
        currentMoment.hours(hours).minutes(minutes).seconds(0);
        valueToSave = currentMoment.format('YYYY-MM-DD HH:mm:ss');
      } else {
        // Invalid time format, cancel edit
        handleCancelEdit();
        return;
      }

      // FIX: When changing one time field, also save the other time field
      // to ensure both are in the same format (without timezone).
      // This prevents timezone mismatch issues where one time has timezone
      // (from original API) and one doesn't (from adjustment).
      const otherField = field === 'time_start' ? 'time_end' : 'time_start';
      const otherTime =
        field === 'time_start' ? getTimeEnd(tt) : getTimeStart(tt);
      const otherMoment = moment(otherTime);
      const otherValueFormatted = otherMoment.format('YYYY-MM-DD HH:mm:ss');

      setAdjustments((prev) => ({
        ...prev,
        [tt.id]: {
          ...prev[tt.id],
          [field]: valueToSave,
          [otherField]: prev[tt.id]?.[otherField] ?? otherValueFormatted,
        },
      }));
      setEditingCell(null);
      setEditValue('');
      return;
    }

    setAdjustments((prev) => ({
      ...prev,
      [tt.id]: {
        ...prev[tt.id],
        [field]: valueToSave,
      },
    }));
    setEditingCell(null);
    setEditValue('');
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Open comment editor popup
  const handleOpenCommentEditor = (tt) => {
    setEditingCommentTT(tt);
    setCommentEditorOpen(true);
  };

  // Close comment editor popup
  const handleCloseCommentEditor = () => {
    setCommentEditorOpen(false);
    setEditingCommentTT(null);
  };

  // Save comment from popup editor
  const handleSaveComment = (html) => {
    if (!editingCommentTT) return;

    setAdjustments((prev) => ({
      ...prev,
      [editingCommentTT.id]: {
        ...prev[editingCommentTT.id],
        comment: html,
      },
    }));
    handleCloseCommentEditor();
  };

  // Apply rounding to all durations (by adjusting end times)
  const handleApplyRounding = (intervalMinutes) => {
    const newAdjustments = { ...adjustments };

    timeTrackings.forEach((tt) => {
      const start = moment(getTimeStart(tt));
      const end = moment(getTimeEnd(tt));

      // Calculate current duration in minutes
      const durationMinutes = end.diff(start, 'minutes');

      // Round duration UP to interval
      const roundedDuration =
        Math.ceil(durationMinutes / intervalMinutes) * intervalMinutes;

      // Only adjust if duration changed
      if (roundedDuration !== durationMinutes) {
        const newEnd = start.clone().add(roundedDuration, 'minutes');
        // Preserve all existing fields when updating
        // FIX: Format time_start consistently to avoid timezone mismatch with time_end
        // Both must be in the same format (local time without timezone indicator)
        newAdjustments[tt.id] = {
          comment: newAdjustments[tt.id]?.comment,
          time_start:
            newAdjustments[tt.id]?.time_start ??
            start.format('YYYY-MM-DD HH:mm:ss'),
          time_end: newEnd.format('YYYY-MM-DD HH:mm:ss'),
        };
      }
    });

    setAdjustments(newAdjustments);
    setRoundingAnchorEl(null);
  };

  // Calculate totals (only non-excluded entries)
  const totalHours = timeTrackings.reduce((sum, tt) => {
    if (isExcludedFromReport(tt)) return sum;
    return sum + getDuration(tt);
  }, 0);

  // Total amount calculation works for both modes
  // CHR: hours × wage preset rate
  // Positions: hours × position price
  const totalAmount = timeTrackings.reduce((sum, tt) => {
    if (isExcludedFromReport(tt)) return sum;
    const preset = presetByTimeTrackingId[tt.id];
    const wage = preset?.wage || 0;
    return sum + getDuration(tt) * wage;
  }, 0);

  // Handle confirm - pass adjustments and calculated hours to parent
  const handleConfirm = () => {
    // Build adjustments map with only actually changed entries (different from original)
    const finalAdjustments = {};
    Object.entries(adjustments).forEach(([id, adj]) => {
      const tt = timeTrackings.find((t) => t.id === parseInt(id));
      if (!tt) return;

      // Build adjustment object with only fields that are actually different
      const realAdj = {};
      if (adj.comment !== undefined && adj.comment !== (tt.comment ?? '')) {
        realAdj.comment = adj.comment;
      }
      if (adj.time_start !== undefined && adj.time_start !== tt.timeStart) {
        realAdj.time_start = adj.time_start;
      }
      if (adj.time_end !== undefined && adj.time_end !== tt.timeEnd) {
        realAdj.time_end = adj.time_end;
      }

      // Only include if there are real changes
      if (Object.keys(realAdj).length > 0) {
        finalAdjustments[id] = {
          ...realAdj,
          exclude_from_report: excludedFromReport[id] || false,
        };
      }
    });

    // Include entries with changed exclude status
    Object.entries(excludedFromReport).forEach(([id, excluded]) => {
      if (excluded && !finalAdjustments[id]) {
        finalAdjustments[id] = {
          exclude_from_report: true,
        };
      } else if (!excluded && !finalAdjustments[id]) {
        // Entry was previously excluded but re-enabled — explicitly clear the flag
        // so handleTimeBillingSave doesn't preserve the old exclude_from_report: true
        const initAdj = initialAdjustments[id] || initialAdjustments[String(id)];
        if (initAdj?.exclude_from_report || initAdj?.excludeFromReport) {
          finalAdjustments[id] = {
            exclude_from_report: false,
          };
        }
      }
    });

    // Calculate hours per preset (excluding excluded entries)
    const presetHoursMap = {};
    selectedPresets.forEach((preset) => {
      const presetEntries = timeTrackings.filter((tt) =>
        (preset.timeTrackingIds || []).includes(tt.id),
      );
      const hours = presetEntries.reduce((sum, tt) => {
        if (isExcludedFromReport(tt)) return sum;
        return sum + getDuration(tt);
      }, 0);
      presetHoursMap[preset.id] = hours;
    });

    // Build per-time-tracking duration map (handles day/time grouping correctly)
    const timeTrackingDurations = {};
    timeTrackings.forEach((tt) => {
      timeTrackingDurations[tt.id] = isExcludedFromReport(tt)
        ? 0
        : getDuration(tt);
    });

    onConfirm(finalAdjustments, presetHoursMap, timeTrackingDurations);
  };

  // Group time trackings by preset for better overview
  const groupedByPreset = selectedPresets.map((preset) => ({
    preset,
    entries: timeTrackings.filter((tt) =>
      (preset.timeTrackingIds || []).includes(tt.id),
    ),
  }));

  // Rounding options
  const roundingOptions = [
    { value: 5, label: t('5 Minutes') },
    { value: 15, label: t('15 Minutes') },
    { value: 30, label: t('30 Minutes') },
    { value: 60, label: t('1 Hour') },
  ];

  // Check if a cell is being edited
  const isEditingCell = (ttId, field) => {
    return editingCell?.id === ttId && editingCell?.field === field;
  };

  // Render editable time cell
  const renderTimeCell = (tt, field, value) => {
    const isEditing = isEditingCell(tt.id, field);
    const isExcluded = isExcludedFromReport(tt);
    const hasAdjustment = hasFieldAdjustment(tt, field);

    if (isEditing) {
      return (
        <TextField
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          size="small"
          autoFocus
          inputProps={{
            style: { width: 50, textAlign: 'center', padding: '4px 8px' },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
            },
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSaveEdit(tt);
            }
            if (e.key === 'Escape') {
              handleCancelEdit();
            }
          }}
          onBlur={() => handleSaveEdit(tt)}
        />
      );
    }

    return (
      <Typography
        component="span"
        className="cursor-pointer"
        onClick={() => handleStartEdit(tt, field)}
        sx={{
          display: 'inline-block',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          transition: 'all 0.15s ease',
          textDecoration: isExcluded ? 'line-through' : 'none',
          backgroundColor: hasAdjustment
            ? 'rgba(33, 150, 243, 0.08)'
            : 'transparent',
          color: hasAdjustment
            ? colors.blueAccent?.[400] || '#2196f3'
            : 'inherit',
          fontWeight: hasAdjustment ? 500 : 'normal',
          '&:hover': {
            backgroundColor: hasAdjustment
              ? 'rgba(33, 150, 243, 0.15)'
              : 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        {moment(value).format('HH:mm')}
      </Typography>
    );
  };

  // Get entries count text with proper pluralization
  const getEntriesCountText = (count) => {
    if (count === 1) {
      return `1 ${t('entry')}`;
    }
    return `${count} ${t('entries')}`;
  };

  return (
    <Box className="flex flex-col gap-4 h-full">
      <Loader active={isLoading} />

      {/* Header */}
      <Box className="flex items-center justify-between px-2">
        <Box className="flex items-center gap-2">
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography fontSize={20} fontWeight={500}>
            {t('Review Time Entries')}
          </Typography>
        </Box>
        <Tooltip title={t('Round End Times')}>
          <IconButton
            onClick={(e) => setRoundingAnchorEl(e.currentTarget)}
            size="small"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <AvTimerIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={roundingAnchorEl}
          open={Boolean(roundingAnchorEl)}
          onClose={() => setRoundingAnchorEl(null)}
          PaperProps={{
            sx: { minWidth: 180, mt: 1 },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              display: 'block',
              opacity: 0.5,
              fontWeight: 500,
            }}
          >
            {t('Round all end times up to')}
          </Typography>
          {roundingOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleApplyRounding(option.value)}
              sx={{ fontSize: 14 }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Summary bar */}
      <Box
        className="flex justify-between items-center px-4 py-3 rounded-lg"
        sx={{
          backgroundColor: colors.bgInput,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {startDate &&
          startDate !== '1970-01-01' &&
          !startDate.startsWith('1970-01-01')
            ? `${moment(startDate).format(config?.general?.dateOutputFormat || 'DD.MM.YYYY')} – ${moment(endDate).format(config?.general?.dateOutputFormat || 'DD.MM.YYYY')}`
            : `${t('until')} ${moment(endDate).format(config?.general?.dateOutputFormat || 'DD.MM.YYYY')}`}
        </Typography>
        <Box className="flex gap-6">
          <Box className="text-right">
            <Typography
              variant="caption"
              sx={{ opacity: 0.5, display: 'block' }}
            >
              {t('Total Hours')}
            </Typography>
            <Typography fontWeight={600}>
              {renderHours(totalHours * 60)} {t('h')}
            </Typography>
          </Box>
          <Box className="text-right">
            <Typography
              variant="caption"
              sx={{ opacity: 0.5, display: 'block' }}
            >
              {t('Total Amount')}
            </Typography>
            <Typography fontWeight={600}>
              <MoneyLabel money={totalAmount} />
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Time entries table */}
      <Box className="flex-1 overflow-y-auto">
        {groupedByPreset.map(({ preset, entries }) => {
          // Calculate preset totals (only non-excluded entries)
          const presetHours = entries.reduce((sum, tt) => {
            if (isExcludedFromReport(tt)) return sum;
            return sum + getDuration(tt);
          }, 0);
          const presetAmount = presetHours * (preset.wage || 0);

          return (
            <Box key={preset.id} className="mb-5">
              {/* Preset/Work Package header */}
              <Box
                className="flex items-center gap-3 px-3 py-2 mb-2"
                sx={{
                  borderRadius: 1,
                  backgroundColor: preset.color + '15',
                  borderLeft: `3px solid ${preset.color}`,
                }}
              >
                <Typography fontWeight={600} sx={{ color: preset.color }}>
                  {preset.label}
                </Typography>
                <Chip
                  label={getEntriesCountText(entries.length)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    backgroundColor: 'transparent',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <Box className="flex-1" />
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  {renderHours(presetHours * 60)} {t('h')} ={' '}
                  <MoneyLabel money={presetAmount} />
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  <MoneyLabel money={preset.wage} /> / {t('h')}
                </Typography>
              </Box>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40} padding="checkbox" align="center">
                        <Tooltip title={t('Show in Report')}>
                          <VisibilityIcon
                            fontSize="small"
                            sx={{ opacity: 0.4, width: 16, height: 16 }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          opacity: 0.7,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('Date')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          opacity: 0.7,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('Employee')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          opacity: 0.7,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('Start')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          opacity: 0.7,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('End')}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 500,
                          opacity: 0.7,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('Duration')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          opacity: 0.7,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('Comment')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((tt) => {
                      const duration = getDuration(tt);
                      const hasAdjustment = hasAnyAdjustment(tt);
                      const isExcluded = isExcludedFromReport(tt);

                      return (
                        <TableRow
                          key={tt.id}
                          sx={{
                            transition: 'all 0.15s ease',
                            opacity: isExcluded ? 0.4 : 1,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            },
                          }}
                        >
                          <TableCell padding="checkbox" align="center">
                            <Checkbox
                              checked={!isExcluded}
                              onChange={() => toggleExcludeFromReport(tt)}
                              size="small"
                              icon={
                                <VisibilityOffIcon
                                  sx={{ width: 18, height: 18, opacity: 0.4 }}
                                />
                              }
                              checkedIcon={
                                <VisibilityIcon
                                  sx={{ width: 18, height: 18 }}
                                />
                              }
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              textDecoration: isExcluded
                                ? 'line-through'
                                : 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {moment(getTimeStart(tt)).format(
                              config?.general?.dateOutputFormat || 'DD.MM.YYYY',
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              textDecoration: isExcluded
                                ? 'line-through'
                                : 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {tt.employee?.firstName} {tt.employee?.lastName}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {renderTimeCell(tt, 'time_start', getTimeStart(tt))}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {renderTimeCell(tt, 'time_end', getTimeEnd(tt))}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              textDecoration: isExcluded
                                ? 'line-through'
                                : 'none',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {renderHours(duration * 60)} {t('h')}
                          </TableCell>
                          <TableCell>
                            <Box
                              className="cursor-pointer flex items-center gap-1"
                              onClick={() => handleOpenCommentEditor(tt)}
                              sx={{
                                minHeight: 24,
                                maxWidth: 300,
                                textDecoration: isExcluded
                                  ? 'line-through'
                                  : 'none',
                                '&:hover .edit-icon': { opacity: 0.5 },
                              }}
                            >
                              {getComment(tt) ? (
                                <Box
                                  sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: 14,
                                    color: hasFieldAdjustment(tt, 'comment')
                                      ? colors.blueAccent?.[400] || '#2196f3'
                                      : 'inherit',
                                    '& p': { margin: 0, display: 'inline' },
                                    '& *': { fontSize: 'inherit' },
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: getComment(tt),
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ flex: 1, opacity: 0.3 }}
                                >
                                  {t('Click to add comment')}
                                </Typography>
                              )}
                              <EditIcon
                                className="edit-icon"
                                sx={{
                                  width: 14,
                                  height: 14,
                                  opacity: 0,
                                  flexShrink: 0,
                                  transition: 'opacity 0.15s ease',
                                }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      </Box>

      {/* Footer buttons */}
      <Box className="flex justify-between p-2">
        <Button
          variant="text"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          {t('Back')}
        </Button>
        <Button
          color="success"
          variant="contained"
          onClick={handleConfirm}
          disabled={timeTrackings.length === 0}
          sx={{ textTransform: 'none', px: 3 }}
        >
          {editMode ? t('Save Changes') : t('Generate Positions')}
        </Button>
      </Box>

      {/* Comment Editor Popup */}
      <PopUpTextEditor
        open={commentEditorOpen}
        onClose={handleCloseCommentEditor}
        onSave={handleSaveComment}
        startHTML={editingCommentTT ? getComment(editingCommentTT) : ''}
      />
    </Box>
  );
};

export default TimeEntryReviewStep;
